import { useField } from 'formik';
import styled, { css } from 'styled-components';
import { ErrorMessage } from 'styles/Styles.styled';
import SmartLabel from '../labels/SmartLabel';
import { HTMLInputAutoCompleteAttribute, HTMLInputTypeAttribute, useId, useState } from 'react';

type Props = {
  name: string;
  label?: string;
  tooltip?: string;
  labelStyle?: any;
  tooltipPosition?: string;
  required?: boolean;
  /**
   * Prevents layout shifts when error appears and disappears
   */
  errorPositioning?: 'absolute';
  error?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  type?: HTMLInputTypeAttribute;
  autoComplete?: HTMLInputAutoCompleteAttribute;
};

const InputFormField: React.FC<Props> = ({
  name,
  label,
  tooltip,
  labelStyle,
  tooltipPosition,
  errorPositioning: errorLocation,
  error,
  ...inputProps
}) => {
  const id = useId();
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!(meta.error || error);
  return (
    <InputWrapper>
      {label && (
        <SmartLabel
          htmlFor={id}
          label={label}
          required={inputProps.required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={field.value}
        />
      )}

      {inputProps.type !== 'password' && <Input id={id} $hasError={hasError} {...field} {...inputProps} />}
      {inputProps.type === 'password' && (
        <PasswordRevealer
          renderInput={(revealed) => (
            <Input id={id} $hasError={hasError} {...field} {...inputProps} type={revealed ? 'text' : 'password'} />
          )}
        />
      )}

      {hasError && <FieldError $absolutePositioning={errorLocation === 'absolute'}>{meta.error || error}</FieldError>}
    </InputWrapper>
  );
};

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PasswordRevealer = ({ renderInput }: { renderInput(isRevealed: boolean): React.ReactNode }) => {
  const [isRevealed, setRevealed] = useState<boolean>(false);

  return (
    <PasswordInputWrapper>
      {renderInput(isRevealed)}
      <RevealPasswordButton
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRevealed(!isRevealed);
        }}
        aria-label="Reveal"
      >
        <i className={isRevealed ? 'pi pi-eye' : 'pi pi-eye-slash'} />
      </RevealPasswordButton>
    </PasswordInputWrapper>
  );
};

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const RevealPasswordButton = styled.button`
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primaryBlack};
  padding: 0;
  background: none;
  border: none;
  margin: 0;
  display: block;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: 1rem;
  border: 1px solid
    ${(props) => {
    if (props.disabled) {
      return props.theme.colors.elementsFilledInput;
    }
    if (props.$hasError) {
      return props.theme.colors.statusLabelRedText;
    }
    return props.theme.colors.elementsInputBorder;
  }};
  border-radius: 2px;
  margin: 0;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  color: ${(props) => (props.disabled ? props.theme.colors.primaryBlack70 : props.theme.colors.black)};
  &:focus {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  height: 44px;
`;

const FieldError = styled(ErrorMessage) <{ $absolutePositioning: boolean }>`
  margin: 0.5rem 0;
  font-family: 'Inter';
  font-size: 12px;
  ${(props) =>
    props.$absolutePositioning &&
    css`
      position: absolute;
      top: 100%;
      margin: 4px 0;
    `}
`;

export default InputFormField;
